import "./PersonalDataForm.scss";

import { useAtom } from "jotai";
import { ReactNode } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Interface/Button/Button";
import { ProfileCard } from "@/components/Layout/BaseLayouts/ProfileCard/ProfileCard";
import {
  BusinessPersonalDetailsFormFields,
  excludedFieldsPrivate,
  fk,
  PersonalDetailsFormFieldKeys,
  PersonalDetailsFormFields,
} from "@/components/Portal/UserAccount/PersonalDetails/PersonalDetailsFormFields/PersonalDetailsFormFields";
import { useAlert } from "@/hooks/useAlert";
import { useUpdateCustomer } from "@/services/api";
import {
  BasicCustomerData,
  IdentificationStatusResponse,
  IdentificationStatusResponseKycStatus,
} from "@/services/model";
import { AlertTypes, userAccountStateAtom } from "@/utils/atoms";
import {
  isBusinessCustomer,
  transformPersonalDetailsFormToBasicCustomerData,
} from "@/utils/customerUtils";
import { log } from "@/utils/log";

export const PersonalDataForm = ({
  localCustomerData,
  setLocalCustomerData,
  formData,
  identificationStatus,
  aside,
}: {
  localCustomerData?: BasicCustomerData;
  setLocalCustomerData: (data: BasicCustomerData) => void;
  formData: Omit<BusinessPersonalDetailsFormFields, "terms">;
  identificationStatus: IdentificationStatusResponse;
  /**
   * Rendered at the bottom of the edit card — used on mobile so the
   * "good to know" stays inside the pinned card instead of sitting in
   * the TwoColumnLayout aside, which the overlay would cover.
   */
  aside?: ReactNode;
}) => {
  const [, setUserAccountState] = useAtom(userAccountStateAtom);
  const showAlert = useAlert();
  const methods = useForm<BusinessPersonalDetailsFormFields>({
    mode: "onChange",
    defaultValues: formData,
  });
  const { mutateAsync: updateCustomer, isLoading: isSaving } =
    useUpdateCustomer();
  const { t, i18n } = useTranslation();
  const baseLanguage = i18n.language.split("-")[0];

  const saveData = (data: BusinessPersonalDetailsFormFields) => {
    if (localCustomerData?.birthday && data) {
      const postBody = transformPersonalDetailsFormToBasicCustomerData(
        data,
        localCustomerData,
        baseLanguage,
        t,
      );

      updateCustomer({
        data: postBody,
      })
        .then(() => {
          setLocalCustomerData(postBody);
          setUserAccountState((prev) => ({
            ...prev,
            editingPersonalData: false,
          }));
          showAlert({
            type: AlertTypes.success,
            text: t("portal:user-account.personalDetails.changeSuccess"),
          });
        })
        .catch((error) => {
          if (`${error?.response?.data}`.includes("invalid phone number")) {
            methods.setError("phone", {
              message: t("label.validation.phoneBackend"),
              type: "backendError",
            });
          } else {
            log("Error in customer form: " + error);
          }
        });
    }
  };

  const isBusiness =
    !!localCustomerData && isBusinessCustomer(localCustomerData);

  const readOnlyFields = [
    ...(identificationStatus?.kycStatus ===
    IdentificationStatusResponseKycStatus.KYC_data_mismatch
      ? [PersonalDetailsFormFieldKeys.formOfAddress]
      : [
          PersonalDetailsFormFieldKeys.formOfAddress,
          PersonalDetailsFormFieldKeys.firstName,
          PersonalDetailsFormFieldKeys.lastName,
          PersonalDetailsFormFieldKeys.dateOfBirth,
        ]),
    // Business: also lock company identity fields
    ...(isBusiness
      ? [fk.organizationName, fk.organizationForm, fk.organizationId]
      : []),
  ];

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(saveData)}>
        <ProfileCard
          className="dire-personal-data-form dire-form-fields--stacked"
          title={t("Onboarding.sections.personal-details.title")}
          stickyHeader
          maxWidth="none"
          actions={
            <>
              <Button
                variant="secondary"
                size="sm"
                type="button"
                disabled={isSaving}
                onClick={() => {
                  methods.reset();
                  setUserAccountState((prev) => ({
                    ...prev,
                    editingPersonalData: false,
                  }));
                }}
              >
                {t("common.buttons.cancel")}
              </Button>
              <Button
                variant="primary"
                size="sm"
                type="submit"
                disabled={isSaving}
                loading={isSaving}
              >
                {t("common.buttons.save")}
              </Button>
            </>
          }
        >
          <PersonalDetailsFormFields
            excludedFields={excludedFieldsPrivate}
            readonlyFields={readOnlyFields}
            disableAllFields={isSaving}
            fieldLayout="stacked"
            addressHeading={
              <h4 className="address-heading text-base font-semibold leading-none text-foreground-primary">
                {t("portal:user-account.personalDetails.address")}
              </h4>
            }
            organizationFieldsPosition={isBusiness ? "top" : "bottom"}
            hideBusinessToggle
            organizationFieldsHeading={
              isBusiness && (
                <h4 className="organization-heading text-base font-semibold leading-none text-foreground-primary">
                  {t(
                    "portal:user-account.personalDetails.companyData.editTitle",
                  )}
                </h4>
              )
            }
          />
          {aside}
        </ProfileCard>
      </form>
    </FormProvider>
  );
};
